/**
 * KAN-744: таблица менеджеров: ФИО ссылкой на карточку, телефон, клиентов,
 * продаж, конверсия, процент с продаж и сумма к выплате (KAN-745). Действия
 * строки (Изменить, В архив / Вернуть, Удалить)
 * передаются только владельцу. KAN-828: у старшего менеджера метка "Старший".
 * KAN-829: у менеджера с привязанным Telegram метка "TG".
 */
import { Link } from 'react-router-dom';
import { Archive, ArchiveRestore, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatClientPhone } from '@/lib/clientListFormat';
import { formatCommission, formatConversion, formatPayout } from '@/lib/managerFormat';
import type { Manager } from '@/types/manager';

export interface ManagerRowActions {
  onEdit: (manager: Manager) => void;
  onToggleActive: (manager: Manager) => void;
  onDelete: (manager: Manager) => void;
}

interface Props {
  managers: Manager[];
  /** Нет: только чтение (не владелец). */
  actions?: ManagerRowActions;
}

function RowActions({ manager, actions }: { manager: Manager; actions: ManagerRowActions }) {
  return (
    <div className="flex justify-end gap-1">
      <Button
        variant="ghost"
        size="icon"
        title="Изменить"
        aria-label="Изменить"
        data-testid={`manager-edit-${manager.id}`}
        onClick={() => actions.onEdit(manager)}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        title={manager.is_active ? 'В архив' : 'Вернуть'}
        aria-label={manager.is_active ? 'В архив' : 'Вернуть'}
        data-testid={`manager-toggle-${manager.id}`}
        onClick={() => actions.onToggleActive(manager)}
      >
        {manager.is_active ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        title="Удалить"
        aria-label="Удалить"
        data-testid={`manager-delete-${manager.id}`}
        onClick={() => actions.onDelete(manager)}
      >
        <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
      </Button>
    </div>
  );
}

export function ManagersTable({ managers, actions }: Props): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-lg border bg-white">
      <Table aria-label="Менеджеры">
        <TableHeader>
          <TableRow>
            <TableHead>ФИО</TableHead>
            <TableHead>Телефон</TableHead>
            <TableHead className="text-right">Клиентов</TableHead>
            <TableHead className="text-right">Продаж</TableHead>
            <TableHead className="text-right">Конверсия</TableHead>
            <TableHead className="text-right">Процент</TableHead>
            <TableHead className="text-right">К выплате</TableHead>
            {actions && (
              <TableHead className="w-px">
                <span className="sr-only">Действия</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {managers.map((m) => (
            <TableRow key={m.id} data-testid={`manager-row-${m.id}`}>
              <TableCell className="font-medium">
                <Link to={`/managers/${m.id}`} className="text-blue-600 hover:underline">
                  {m.full_name}
                </Link>
                {m.is_senior && (
                  <Badge variant="secondary" className="ml-2" data-testid={`manager-senior-${m.id}`}>
                    Старший
                  </Badge>
                )}
                {m.telegram_linked && (
                  <Badge variant="outline" className="ml-2" title="Telegram подключён" data-testid={`manager-tg-${m.id}`}>
                    TG
                  </Badge>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {m.phone ? formatClientPhone(m.phone) : <span className="text-gray-400">-</span>}
              </TableCell>
              <TableCell className="text-right tabular-nums">{m.clients_count}</TableCell>
              <TableCell className="text-right tabular-nums">{m.sales_count}</TableCell>
              <TableCell className="text-right tabular-nums">
                {formatConversion(m.conversion_pct)}
              </TableCell>
              <TableCell
                className="whitespace-nowrap text-right tabular-nums"
                data-testid={`manager-commission-${m.id}`}
              >
                {formatCommission(m.commission_pct)}
              </TableCell>
              <TableCell
                className="whitespace-nowrap text-right tabular-nums"
                data-testid={`manager-payout-${m.id}`}
              >
                {formatPayout(m.payout)}
              </TableCell>
              {actions && (
                <TableCell>
                  <RowActions manager={m} actions={actions} />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
